import {
  differenceInMonths,
  differenceInYears,
  format,
  parse,
} from 'date-fns'
import i18next from 'i18next'
import { declensionByNum, getDateFnsLocaleByIso } from 'www/utils'
import { MONTH_CASES, YEAR_CASES } from 'www/constants/wordCases'

/**
 * @typedef PatientData
 * @property { string } surname -
 * @property { string } name -
 * @property { string } patronymic -
 * @property { Date | string | null } birthDay -
 */

/**
 * @typedef PartialPatientData
 * @property { string } fio -
 * @property { string } age -
 * @property { string } birthDay -
 */

/**
 * @description Возвращает отформатированный возраст пациента - в годах или месяцах
 * @param { PatientData } patientData -
 * @param { Date | string | null } patientData.birthDay - дата рождения пациента
 * @param { boolean } isYearOnly - получить только год пациента
 *
 * @returns { number | string | '' } - Отформатированный возраст пациента - в годах или месяцах, либо пустая строка.
 * Если поле "isYearOnly = true" - вернется число(0 или возраст пациента в годах).
 * Если поле "isYearOnly = false" - вернется форматированная или пустая строка.
 */
const getPatientAge = ({ birthDay }, isYearOnly = false) => {
  if (!birthDay) {
    return isYearOnly ? 0 : ''
  }

  const { language } = i18next
  const options = { ns: language }
  const now = new Date()
  const birthDayDate = typeof birthDay === 'string'
    ? parse(birthDay, 'dd.MM.yyyy', now, { locale: getDateFnsLocaleByIso(language) })
    : birthDay
  const ageYears = differenceInYears(now, birthDayDate)

  if (ageYears >= 1) {
    return isYearOnly ? ageYears : `${ageYears} ${i18next.t(declensionByNum(ageYears, YEAR_CASES.nominative), options)}`
  }

  if (isYearOnly) {
    return 0
  }

  const ageMonths = differenceInMonths(now, birthDayDate)

  return ageMonths
    ? `${ageMonths} ${i18next.t(declensionByNum(ageMonths, MONTH_CASES.nominative), options)}`
    : ''
}

const getPatientFio = ({ surname, name, patronymic }) => {
  const lowerCaseSurname = surname.toLowerCase()
  const transformedSurname = lowerCaseSurname
    ? lowerCaseSurname.replace(lowerCaseSurname[0], lowerCaseSurname[0].toUpperCase())
    : ''

  const lowerCaseName = name.toLowerCase()
  const transformedName = lowerCaseName
    ? lowerCaseName.replace(lowerCaseName[0], lowerCaseName[0].toUpperCase())
    : ''

  let transformedPatronymic = patronymic

  if (patronymic) {
    const lowerCasePatronymic = patronymic.toLowerCase()

    transformedPatronymic = lowerCasePatronymic
      .replace(lowerCasePatronymic[0], lowerCasePatronymic[0].toUpperCase())
  }

  return `${transformedSurname} ${transformedName} ${transformedPatronymic}`.trim()
}

/**
 * @description Возвращает форматированный возраст, ФИО и день рождения пациента
 * @param { PatientData } patientData -
 *
 * @returns {object} -
 */
const getPartialPatientData = patientData => {
  const age = getPatientAge(patientData)
  const ageNumber = getPatientAge(patientData, true)
  const fio = getPatientFio(patientData)

  let birthDay = ''

  if (patientData.birthDay) {
    birthDay = typeof patientData.birthDay === 'string'
      ? patientData.birthDay
      : format(patientData.birthDay, 'dd.MM.yyyy', { locale: getDateFnsLocaleByIso(i18next.language) })
  }

  return {
    age,
    fio,
    ageNumber,
    birthDay,
  }
}

export default getPartialPatientData
